/**
 * §4.8, per node: **who can reach this, and why** — which grant, at which
 * level, with what precedence.
 *
 * Every row on this page came out of `permission_resolve_node`, the same
 * function that authorized the last mutation anyone made. Nothing here compares
 * a distance or reads a grant row: the columns rendered are the columns §4.7
 * returns, and the only work this file does on top of them is turning a uuid
 * into a name.
 *
 * Denied people are listed too. "Who cannot reach this, and which grant stopped
 * them" is the same question from the other side, and it is the one asked when
 * somebody reports they cannot see something — §8.5 puts that first on the list
 * of things support checks.
 */

import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useNode, useNodeAccess, useSpaceNodes, useSpaces } from '../../api/queries.ts';
import {
  CAPABILITY_MEANING,
  CAPABILITY_OPTIONS,
  isKnownCapability,
} from '../../components/capabilities.ts';
import {
  GrantSite,
  PrecedenceChips,
  PrecedenceLegend,
  type SubjectNamer,
  VerdictBadge,
} from '../../components/decision.tsx';
import { RoleBadge, StatusBadge } from '../../components/ui/badge.tsx';
import { LinkButton } from '../../components/ui/button.tsx';
import { Alert, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Select } from '../../components/ui/field.tsx';
import { Card, CardHeader, Mono, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';

export function NodeAccessPage() {
  const { nodeId } = useParams({ from: '/_authed/schema/access/nodes/$nodeId' });
  const search = useSearch({ from: '/_authed/schema/access/nodes/$nodeId' });
  const navigate = useNavigate();

  const capability = search.capability ?? 'document.view';
  const access = useNodeAccess(nodeId, capability);
  const node = useNode(nodeId);
  const spaces = useSpaces();
  const siblings = useSpaceNodes(node.data?.node.spaceId ?? '', 'document.view');

  /**
   * A grant sits on a space or a node, and the resolver returns its id. Naming
   * it is presentation: the space list and this space's node list are already
   * loaded, and anything not in either falls back to the raw id rather than
   * being guessed at.
   */
  const nameOf: SubjectNamer = useMemo(() => {
    const spaceNames = new Map((spaces.data?.spaces ?? []).map((space) => [space.id, space.name]));
    const nodeNames = new Map(
      (siblings.data?.nodes ?? []).map((sibling) => [sibling.id, sibling.title]),
    );
    if (node.data) nodeNames.set(node.data.node.id, node.data.node.title);
    return (type, id) => (type === 'space' ? spaceNames.get(id) : nodeNames.get(id));
  }, [spaces.data, siblings.data, node.data]);

  if (access.isPending) return <Loading />;
  if (access.isError) {
    return (
      <PageError
        error={access.error}
        title="Who can reach this"
        back={{ href: `/nodes/${nodeId}`, label: 'Back to the node' }}
      />
    );
  }

  const entries = access.data.entries;
  const allowed = entries.filter((entry) => entry.decision.allowed);
  const denied = entries.filter((entry) => !entry.decision.allowed);

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/nodes/$nodeId" params={{ nodeId }} className="text-tertiary no-underline">
            {access.data.node.title}
          </Link>
        }
        title="Who can reach this"
        description={`Every person in the system, resolved against this node for one capability. ${allowed.length} allowed, ${denied.length} not.`}
        actions={
          <LinkButton variant="secondary" href={`/spaces/${access.data.node.spaceId}`}>
            Open the space
          </LinkButton>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <Select
            label="Capability"
            className="max-w-md"
            options={CAPABILITY_OPTIONS}
            selectedKey={capability}
            onSelectionChange={(value) =>
              void navigate({
                to: '/schema/access/nodes/$nodeId',
                params: { nodeId },
                search: { capability: value },
                replace: true,
              })
            }
            description={isKnownCapability(capability) ? CAPABILITY_MEANING[capability] : undefined}
          />
          <p className="mt-3 text-sm text-tertiary">
            Resolution is per capability. A person allowed{' '}
            <span className="font-mono">document.view</span> here may be denied{' '}
            <span className="font-mono">document.edit</span> on the same node, and the two are
            answered by separate walks up the same chain.
          </p>
        </Card>

        <Card>
          <h2 className="mb-2 text-sm font-semibold text-primary">Precedence</h2>
          <PrecedenceLegend />
        </Card>
      </div>

      <Card padded={false}>
        <CardHeader
          title="Allowed"
          description="Sorted by how near the deciding grant sits, which is the first term of the ordering that decided it."
        />
        <Table>
          <THead>
            <TR>
              <TH>Person</TH>
              <TH>Verdict</TH>
              <TH>Deciding grant sits on</TH>
              <TH>Precedence</TH>
              <TH>Why</TH>
            </TR>
          </THead>
          <TBody>
            {allowed.length === 0 ? (
              <TableEmpty colSpan={5}>
                Nobody can reach this node with <span className="font-mono">{capability}</span>.
              </TableEmpty>
            ) : (
              allowed.map((entry) => (
                <TR key={entry.person.id} className="hover:bg-secondary">
                  <TD>
                    <Link
                      to="/admin/people/$personId"
                      params={{ personId: entry.person.id }}
                      className="font-medium text-primary no-underline hover:underline"
                    >
                      {entry.person.name ?? entry.person.id}
                    </Link>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      {entry.person.role ? <RoleBadge role={entry.person.role} /> : null}
                      {entry.person.status ? <StatusBadge status={entry.person.status} /> : null}
                    </span>
                  </TD>
                  <TD>
                    <VerdictBadge decision={entry.decision} />
                  </TD>
                  <TD>
                    <GrantSite decision={entry.decision} nameOf={nameOf} />
                  </TD>
                  <TD>
                    <PrecedenceChips decision={entry.decision} />
                  </TD>
                  <TD className="max-w-sm text-xs">
                    {entry.explanation}
                    {entry.decision.grantId ? (
                      <Mono className="mt-1 block">{entry.decision.grantId}</Mono>
                    ) : null}
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </Card>

      <Card padded={false}>
        <CardHeader
          title="Not allowed"
          description="An explicit deny and no grant at all are both denied, and the difference is the point: one is a decision somebody made, the other is rule 4."
        />
        <Table>
          <THead>
            <TR>
              <TH>Person</TH>
              <TH>Verdict</TH>
              <TH>Deciding grant sits on</TH>
              <TH>Precedence</TH>
              <TH>Why</TH>
            </TR>
          </THead>
          <TBody>
            {denied.length === 0 ? (
              <TableEmpty colSpan={5}>Everyone can reach this node.</TableEmpty>
            ) : (
              denied.map((entry) => (
                <TR key={entry.person.id} className="hover:bg-secondary">
                  <TD>
                    <Link
                      to="/admin/people/$personId"
                      params={{ personId: entry.person.id }}
                      className="font-medium text-primary no-underline hover:underline"
                    >
                      {entry.person.name ?? entry.person.id}
                    </Link>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      {entry.person.role ? <RoleBadge role={entry.person.role} /> : null}
                      {entry.person.status ? <StatusBadge status={entry.person.status} /> : null}
                    </span>
                  </TD>
                  <TD>
                    <VerdictBadge decision={entry.decision} />
                  </TD>
                  <TD>
                    <GrantSite decision={entry.decision} nameOf={nameOf} />
                  </TD>
                  <TD>
                    <PrecedenceChips decision={entry.decision} />
                  </TD>
                  <TD className="max-w-sm text-xs">{entry.explanation}</TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </Card>

      <Alert tone="info">
        A deactivated person is denied here regardless of any grant, because the resolver's actor
        step filters <span className="font-mono">status = 'active'</span> before it looks at the
        chain. That is what makes offboarding hold for role-based reach, which cannot be revoked row
        by row.
      </Alert>
    </>
  );
}
